import { z } from "zod";

import { cookieDomain, readConsent, recordImpliedConsent, type ConsentCategory } from "../consent/consent";

import { attributionSchema, categoryAttribution, type CheckoutAttribution } from "./attribution";

const stateSchema = z.object({
  v: z.literal(1), id: z.uuid(),
  sent: z.array(z.string().max(160).regex(/^[A-Za-z0-9:_-]+$/)).max(64),
  pages: z.array(z.string().max(160).regex(/^[A-Za-z0-9:_-]+$/)).max(8),
  auth: z.array(z.string().max(160).regex(/^[A-Za-z0-9:_-]+$/)).max(16).default([]),
});
export type TrackingState = z.infer<typeof stateSchema>;
export interface TrackingStore {
  allowed(category: ConsentCategory): boolean;
  readAttribution(category: ConsentCategory): CheckoutAttribution;
  writeAttribution(category: ConsentCategory, value: CheckoutAttribution): boolean;
  read(category: ConsentCategory): TrackingState | null;
  write(category: ConsentCategory, state: TrackingState): boolean;
}
const names = { analytics: "cai_first_ad_analytics", advertising: "cai_first_ad_ads" };
const receiptNames = { analytics: "cai_first_ad_analytics_receipts", advertising: "cai_first_ad_ads_receipts" };

export function readCookie(name: string): string | undefined {
  const raw = document.cookie.split(/;\s*/).find((part) => part.startsWith(`${name}=`))?.slice(name.length + 1);
  try { return raw === undefined ? undefined : decodeURIComponent(raw); } catch { return undefined; }
}

function writeCookie(name: string, value: string, seconds: number): boolean {
  const domain = cookieDomain(window.location.hostname);
  const attributes = `; path=/; max-age=${seconds}; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
  if (domain !== null) {
    document.cookie = `${name}=; path=/; max-age=0`;
    document.cookie = `${name}=${encodeURIComponent(value)}${attributes}; domain=${domain}`;
    if (seconds > 0 && readCookie(name) === value) return true;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}${attributes}`;
  return seconds === 0 || readCookie(name) === value;
}

/** Tracking cookies are separate from ctv_consent; its existing resolution rules stay authoritative. */
export const cookieStore: TrackingStore = {
  allowed(category) {
    recordImpliedConsent();
    const allowed = readConsent()?.[category] === true;
    if (!allowed) {
      writeCookie(names[category], "", 0);
      writeCookie(receiptNames[category], "", 0);
      if (category === "advertising") {
        writeCookie("_fbp", "", 0);
        writeCookie("_fbc", "", 0);
      }
    }
    return allowed;
  },
  readAttribution(category) {
    if (!this.allowed(category)) return {};
    const raw = readCookie(names[category]);
    if (raw === undefined || raw.length > 2500) return {};
    try {
      const parsed = attributionSchema.safeParse(JSON.parse(raw));
      return parsed.success ? categoryAttribution(parsed.data, category) : {};
    } catch { return {}; }
  },
  writeAttribution(category, value) {
    if (!this.allowed(category)) return false;
    const raw = JSON.stringify(categoryAttribution(value, category));
    return raw.length <= 2500 && writeCookie(names[category], raw, 60 * 60 * 24 * 30);
  },
  read(category) {
    if (!this.allowed(category)) return null;
    const raw = readCookie(receiptNames[category]);
    if (raw === undefined || raw.length > 3500) return null;
    try {
      const parsed = stateSchema.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : null;
    } catch { return null; }
  },
  write(category, state) {
    if (!this.allowed(category)) return false;
    const raw = JSON.stringify(state);
    return raw.length <= 3500 && writeCookie(receiptNames[category], raw, 60 * 60 * 24 * 30);
  },
};
